import React, { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

import { API_URL } from '../utils/apiBase';
import { safeSessionGet, safeSessionRemove } from '../utils/safeStorage';

/**
 * Auth callback handler — runs after Emergent's Google OAuth provider
 * redirects back to /auth/callback#session_id=<token>
 *
 * Steps:
 *   1. Read session_id from URL fragment
 *   2. POST it to /api/auth/session (server exchanges for persistent token + cookie)
 *   3. Bounce back to home (or to the saved returnTo)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE EMERGENT AUTH.
 */
export default function AuthCallback() {
  const { t } = useLanguage();
  const [status, setStatus] = useState('processing');
  const [errorMsg, setErrorMsg] = useState('');
  // useRef guard — prevents double-fire (StrictMode dev double-invoke OR a
  // browser navigation race). Emergent's /session-data endpoint is one-shot,
  // so a duplicate call would 404 and burn the user's session_id.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const fragment = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(fragment);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setErrorMsg('Missing session_id in callback URL.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Auth exchange failed (${res.status})`);
        }
        if (cancelled) return;
        setStatus('success');

        // Bounce back to where the user wanted to go (or home)
        const returnTo = safeSessionGet('post_login_return') || '/';
        safeSessionRemove('post_login_return');

        // Small delay so the user sees the success state, then navigate.
        setTimeout(() => {
          window.location.replace(returnTo);
        }, 600);
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(err.message || 'Sign-in failed. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4"
      data-testid="auth-callback-page"
    >
      <div className="bg-white rounded-2xl shadow-xl p-10 max-w-sm w-full text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-1">{t('authCallback.signingIn')}</h1>
            <p className="text-sm text-gray-500">{t('authCallback.almostThere')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-1">{t('authCallback.welcome')}</h1>
            <p className="text-sm text-gray-500">{t('authCallback.taking')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-1">{t('authCallback.failed')}</h1>
            <p className="text-sm text-gray-600 mb-6">{errorMsg}</p>
            <a
              href="/"
              data-testid="auth-callback-home-link"
              className="inline-block px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              {t('authCallback.backHome')}
            </a>
          </>
        )}
      </div>
    </div>
  );
}
